import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setLocaleResolver, setErrorTranslator } from '@noor/api';
import ruErrors from './locales/ru/errors.json';
import enErrors from './locales/en/errors.json';
import uzErrors from './locales/uz/errors.json';
import ruCommon from './locales/ru/common.json';
import enCommon from './locales/en/common.json';
import uzCommon from './locales/uz/common.json';
import ruEats from './locales/ru/eats.json';
import enEats from './locales/en/eats.json';
import uzEats from './locales/uz/eats.json';
import ruDelivery from './locales/ru/delivery.json';
import enDelivery from './locales/en/delivery.json';
import uzDelivery from './locales/uz/delivery.json';
// The `legacy` namespace backs the toggleable reproduction of the old merchant
// panel. Its strings are transcribed from that app's own translation files, so
// legacy screens read exactly as they did before — they are deliberately NOT
// merged into `delivery`, whose wording the new UI is free to change.
import ruLegacy from './locales/ru/legacy.json';
import enLegacy from './locales/en/legacy.json';
import uzLegacy from './locales/uz/legacy.json';

// The platform targets a RU/UZ/EN market; Russian is the source of truth and
// the default/fallback language. The active language is a durable pref in the
// `ui-store`; the language button calls `i18n.changeLanguage`.
export const I18N_NAMESPACES = [
  'common',
  'eats',
  'delivery',
  'legacy',
  // Backend error catalog: one key per numeric code the API can return. Owned
  // by `@noor/api`'s `error-catalog.ts` — see `setErrorTranslator` below.
  'errors',
] as const;

const resources = {
  ru: {
    common: ruCommon,
    eats: ruEats,
    delivery: ruDelivery,
    legacy: ruLegacy,
    errors: ruErrors,
  },
  en: {
    common: enCommon,
    eats: enEats,
    delivery: enDelivery,
    legacy: enLegacy,
    errors: enErrors,
  },
  uz: {
    common: uzCommon,
    eats: uzEats,
    delivery: uzDelivery,
    legacy: uzLegacy,
    errors: uzErrors,
  },
} as const;

/** Read the persisted language pref synchronously so boot uses the right lang. */
function initialLanguage(): string {
  if (typeof localStorage === 'undefined') return 'ru';
  try {
    const raw = localStorage.getItem('ui-prefs');
    if (!raw) return 'ru';
    const parsed = JSON.parse(raw) as { state?: { language?: string } };
    return parsed.state?.language ?? 'ru';
  } catch {
    return 'ru';
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: 'ru',
  defaultNS: 'common',
  ns: I18N_NAMESPACES,
  interpolation: { escapeValue: false },
});

// Keep <html lang> truthful for screen readers / translators — index.html
// ships a static `lang="en"` regardless of the active UI language. Fires on
// init and on every switch.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});
document.documentElement.lang = i18n.language;

// Teach the API client which language to ask the backend for (`x-locale`).
// i18n is the source of truth rather than the `ui-store` directly: the store
// only holds the pref, while `i18n.language` is what actually resolved after
// fallback, and it is what the UI is rendering. A GETTER, not a value, so a
// language switch takes effect on the next request with no re-registration.
//
// Wired here because this module is imported for its side effects at boot,
// before any query can fire — the same reason the `<html lang>` sync lives here.
setLocaleResolver(() => i18n.language);

// Teach the API layer to translate backend error CODES. `getApiErrorMessage`
// consults this before falling back to the call site's own string, so all ~50
// existing call sites became localized without being touched.
//
// Returns `undefined` for a key this app has no string for (a code newer than
// the bundle) rather than i18next's default of echoing the key back — that is
// what lets resolution fall through to the caller's fallback instead of
// rendering `errors:product.notFound` at a merchant.
setErrorTranslator((key) => {
  const translated = i18n.t(key, { ns: 'errors', defaultValue: '' });
  return translated === '' ? undefined : translated;
});

export { i18n };
